import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Globe, ArrowRight, Check, Copy, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Compact home-page nudge for tenant_admin:
 *  - If no subdomain claimed → "Claim your branded URL" CTA
 *  - If claimed              → show the URL + copy
 */
export default function HomeBrandBanner() {
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get("/tenant/branding").then((r) => setB(r.data)).catch(() => {});
  }, []);

  if (!b) return null;

  if (!b.subdomain) {
    const root = b.root_domain || "axia.app";
    return (
      <Link
        to="/admin/settings"
        className="block axia-card p-5 hover:border-slate-400 transition group"
        data-testid="home-brand-cta"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-9 h-9 rounded-md bg-violet-50 text-violet-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm font-semibold text-slate-900">Claim your branded URL</div>
            <p className="text-xs text-slate-500 mt-0.5">Pick <code className="font-mono">acme.{root}</code> so kudos and shared review links are on your brand.</p>
          </div>
          <Button size="sm" variant="outline" className="group-hover:border-slate-900">
            Claim now <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </Link>
    );
  }

  return (
    <div className="axia-card p-5" data-testid="home-brand-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <Globe className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="axia-overline">Your branded URL</div>
          <code className="font-mono text-sm text-slate-900">{b.subdomain_url}</code>
        </div>
        <CopyBtn text={b.subdomain_url} />
        <Link to="/admin/settings" className="text-xs text-slate-500 hover:text-slate-900">Manage →</Link>
      </div>
    </div>
  );
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={async () => {
        await navigator.clipboard.writeText(text || "");
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      data-testid="home-brand-copy"
    >
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}
